/**
 * StudentAttendanceTab — embedded inside StudentDetail's "Attendance" tab
 * via render-prop injection (Prompt 11E.6), same pattern as
 * StudentBookingsTab/StudentBillingTab/StudentEntitlementsTab.
 *
 * Period filter (this week / this month / last N months / custom range,
 * Classcard-style) + attended/missed/excused counts + per-row teacher,
 * remaining-reschedules, and a Reschedule action.
 *
 * The Reschedule button itself is injected via `renderRescheduleAction`
 * (implemented in Bookings — see RescheduleBookingButton — and wired at
 * the route composition layer) because the ESLint feature-boundary rule
 * forbids Attendance importing from Bookings directly.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Card, Button, Dropdown, Input, Badge, Table } from '@/components/common';
import {
  useStudentAttendance,
  type AttendancePeriod,
  type StudentAttendanceRow,
} from '@features/attendance/api/studentAttendance';

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
`;

const FilterBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const statusColor: Record<string, string> = {
  present: '#16a34a',
  late: '#16a34a',
  makeup: '#16a34a',
  absent: '#dc2626',
  excused: '#64748b',
};

const PERIOD_OPTIONS: { value: AttendancePeriod; label: string }[] = [
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_n_months', label: 'Last N months' },
  { value: 'custom', label: 'Custom range' },
];

interface Props {
  studentId: string;
  renderRescheduleAction?: (row: StudentAttendanceRow) => React.ReactNode;
}

export const StudentAttendanceTab: React.FC<Props> = ({ studentId, renderRescheduleAction }) => {
  const [period, setPeriod] = useState<AttendancePeriod>('this_month');
  const [nMonths, setNMonths] = useState(3);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data, isLoading, isError } = useStudentAttendance(studentId, {
    period,
    nMonths: period === 'last_n_months' ? nMonths : undefined,
    from: period === 'custom' && fromDate ? new Date(fromDate).toISOString() : undefined,
    to: period === 'custom' && toDate ? new Date(toDate).toISOString() : undefined,
  });

  const rows = data?.rows ?? [];

  return (
    <div data-testid="candeza-student-attendance-tab">
      <FilterBar>
        <Dropdown
          label="Period"
          options={PERIOD_OPTIONS}
          value={period}
          onChange={(v) => setPeriod(v as AttendancePeriod)}
          testId="candeza-attendance-filter"
        />
        {period === 'last_n_months' && (
          <Input
            label="N months"
            type="number"
            min={1}
            max={24}
            value={nMonths}
            onChange={(e) => setNMonths(Number(e.target.value) || 1)}
            data-testid="candeza-attendance-filter-last_n_months-n"
            style={{ width: 100 }}
          />
        )}
        {period === 'custom' && (
          <>
            <Input
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="candeza-attendance-filter-custom-from"
            />
            <Input
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="candeza-attendance-filter-custom-to"
            />
          </>
        )}
      </FilterBar>

      <StatsRow data-testid={`candeza-attendance-filter-${period}`}>
        <StatBox>
          <StatLabel>Attended</StatLabel>
          <StatValue style={{ color: '#16a34a' }} data-testid="candeza-attendance-count-attended">
            {data?.attended ?? '—'}
          </StatValue>
        </StatBox>
        <StatBox>
          <StatLabel>Missed / no-show</StatLabel>
          <StatValue style={{ color: '#dc2626' }} data-testid="candeza-attendance-count-missed">
            {data?.missed ?? '—'}
          </StatValue>
        </StatBox>
        <StatBox>
          <StatLabel>Excused</StatLabel>
          <StatValue data-testid="candeza-attendance-count-excused">
            {data?.excused ?? '—'}
          </StatValue>
        </StatBox>
      </StatsRow>

      <Card padding="lg">
        {isError ? (
          <p style={{ color: '#dc2626' }}>Could not load attendance for this period.</p>
        ) : (
          <Table<StudentAttendanceRow>
            data={rows}
            isLoading={isLoading}
            emptyMessage="No attendance records in this period."
            testId="candeza-student-attendance-table"
            getRowTestId={(row) => `candeza-student-attendance-row-${row.id}`}
            columns={[
              {
                key: 'session_start',
                header: 'Date',
                isPrimary: true,
                render: (row) =>
                  row.session_start
                    ? new Date(row.session_start).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—',
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => (
                  <Badge
                    size="sm"
                    variant="default"
                    style={{ color: statusColor[row.status] ?? undefined }}
                  >
                    {row.status}
                  </Badge>
                ),
              },
              {
                key: 'notes',
                header: 'Remarks',
                hideOnMobile: true,
                // "No-show" has no distinct status in this system — it's
                // recorded as a remark on an 'absent' row (see
                // docs/04-modules/attendance.md).
                render: (row) => row.notes ?? '—',
              },
              {
                key: 'teacher_name',
                header: 'Teacher',
                hideOnMobile: true,
                render: (row) => row.teacher_name ?? '—',
              },
              {
                key: 'remaining_reschedules',
                header: 'Reschedules left',
                hideOnMobile: true,
                render: (row) => row.remaining_reschedules ?? '—',
              },
              {
                key: 'actions',
                header: '',
                render: (row) =>
                  renderRescheduleAction ? (
                    renderRescheduleAction(row)
                  ) : (
                    <Button size="sm" variant="outline" disabled>
                      Reschedule
                    </Button>
                  ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default StudentAttendanceTab;
